import React, { useState, useEffect, useRef } from 'react';
import { Flex, Box } from '../design-system';
import { Table } from './Table';
import SearchFilter from './SearchFilter';
import { usePaginatedData } from '../hooks/usePaginatedData';

/**
 * Enhanced PaginatedTable component with search functionality
 * @param {string} endpoint - The API endpoint for data
 * @param {Array} columns - Table columns configuration
 * @param {string} name - Table name for display
 * @param {Object} dataOptions - Options for data loading (errorMsg, etc.)
 * @param {number} initialPageSize - Initial page size (default: 30)
 * @param {number} initialPage - Initial page number (default: 1)
 * @param {Function} onDataChange - Callback when data changes
 * @param {boolean} enableSearch - Whether to show search filter (default: false)
 * @param {string} searchPlaceholder - Placeholder for search input
 * @param {Object} tableProps - Additional props to pass to Table component
 */
interface PaginatedTableWithSearchProps {
  endpoint: string;
  columns: any[];
  name?: string;
  dataOptions?: Record<string, any>;
  initialPageSize?: number;
  initialPage?: number;
  initialSortColumn?: string;
  initialSortDesc?: boolean;
  onDataChange?: (
    data: any,
    fetchData: any,
    loading: boolean,
    error: any,
  ) => void;
  enableSearch?: boolean;
  searchPlaceholder?: string;
  [key: string]: any;
}

const PaginatedTableWithSearch = ({
  endpoint,
  columns,
  name,
  dataOptions = {},
  initialPageSize = 30,
  initialPage = 1,
  initialSortColumn,
  initialSortDesc = false,
  onDataChange,
  enableSearch = false,
  searchPlaceholder = 'Search...',
  ...tableProps
}: PaginatedTableWithSearchProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounce search term to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Build endpoint with search parameter
  const searchEndpoint = debouncedSearchTerm
    ? `${endpoint}?search=${encodeURIComponent(debouncedSearchTerm)}`
    : endpoint;

  const [
    data,
    fetchData,
    loading,
    error,
    pagination,
    setPagination,
    handlePageChange,
  ] = usePaginatedData(
    searchEndpoint,
    dataOptions,
    initialPageSize,
    initialPage,
    initialSortColumn ?? null,
    initialSortDesc,
  );

  // Reset to first page when search changes
  useEffect(() => {
    // Always reset to page 1 when search changes
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [debouncedSearchTerm, setPagination]);

  // Refetch when the debounced search term changes. Keep the latest fetchData
  // in a ref so this effect does NOT re-fire on every pagination change (which
  // otherwise recreates fetchData's identity and triggers a spurious refetch).
  const fetchDataRef = useRef(fetchData);
  useEffect(() => {
    fetchDataRef.current = fetchData;
  });
  useEffect(() => {
    if (fetchDataRef.current) {
      fetchDataRef.current();
    }
  }, [debouncedSearchTerm]);

  // Call onDataChange when data changes
  useEffect(() => {
    if (onDataChange && data) {
      onDataChange(data, fetchData, loading, error);
    }
  }, [data, fetchData, loading, error, onDataChange]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  return (
    <>
      {enableSearch && (
        <Flex
          justifyContent="flex-start"
          alignItems="center"
          p="0 36px 18px 26px"
        >
          <Box width={250}>
            <SearchFilter
              onFilter={handleSearch}
              placeholder={searchPlaceholder}
            />
          </Box>
        </Flex>
      )}
      <Table
        name={name}
        columns={columns}
        data={data || []}
        loading={loading}
        onPageChange={handlePageChange}
        pagination={true}
        initialSortBy={
          initialSortColumn
            ? [{ id: initialSortColumn, desc: initialSortDesc }]
            : []
        }
        {...tableProps}
      />
    </>
  );
};

export default PaginatedTableWithSearch;
